import { useState, useEffect, useCallback } from "react";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { CreditCard, Plus, PencilSimple, Trash, CheckCircle, X, CurrencyGbp, Clock, Users, Folder, Truck, HardDrive, EnvelopeSimple, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY_FORM = {
  id: "",
  name: "",
  monthly_price: "",
  annual_price: "",
  one_time_price: "",
  trial_days: 7,
  features: [""],
  limits: { users: 5, projects: 10, suppliers: 25, storage_gb: 2 },
};

export default function SubscriptionPlansPage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editPlan, setEditPlan] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [deletePlan, setDeletePlan] = useState(null);
  const [tenantCounts, setTenantCounts] = useState({});
  const [trialNotifications, setTrialNotifications] = useState([]);
  const [sendingNotifications, setSendingNotifications] = useState(false);

  const fetchPlans = useCallback(async () => {
    try {
      const { data } = await API.get("/subscription-plans");
      setPlans(data.plans || []);
    } catch {
      toast.error("Failed to load plans");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTenantCounts = useCallback(async () => {
    try {
      const { data } = await API.get("/tenants");
      const counts = {};
      (Array.isArray(data) ? data : []).forEach(t => {
        counts[t.plan] = (counts[t.plan] || 0) + 1;
      });
      setTenantCounts(counts);
    } catch {}
  }, []);

  useEffect(() => {
    fetchPlans();
    fetchTenantCounts();
    API.get("/trial-notifications").then(({ data }) => setTrialNotifications(data.notifications || [])).catch(() => {});
  }, [fetchPlans, fetchTenantCounts]);

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, features: [""] });
    setEditPlan(null);
    setShowCreate(true);
  };

  const openEdit = (plan) => {
    setForm({
      id: plan.id,
      name: plan.name,
      monthly_price: plan.monthly_price ?? "",
      annual_price: plan.annual_price ?? "",
      one_time_price: plan.one_time_price ?? "",
      trial_days: plan.trial_days ?? 7,
      features: plan.features?.length ? [...plan.features] : [""],
      limits: { ...EMPTY_FORM.limits, ...plan.limits },
    });
    setEditPlan(plan);
    setShowCreate(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const features = form.features.filter(f => f.trim());
      const payload = {
        name: form.name,
        monthly_price: form.monthly_price !== "" ? parseFloat(form.monthly_price) : null,
        annual_price: form.annual_price !== "" ? parseFloat(form.annual_price) : null,
        one_time_price: form.one_time_price !== "" ? parseFloat(form.one_time_price) : null,
        trial_days: parseInt(form.trial_days) || 0,
        features,
        limits: {
          users: parseInt(form.limits.users) || -1,
          projects: parseInt(form.limits.projects) || -1,
          suppliers: parseInt(form.limits.suppliers) || -1,
          storage_gb: parseInt(form.limits.storage_gb) || -1,
        },
      };

      if (editPlan) {
        await API.put(`/subscription-plans/${editPlan.id}`, payload);
        toast.success(`Plan "${form.name}" updated`);
      } else {
        payload.id = form.id || form.name.toLowerCase().replace(/\s+/g, "_");
        await API.post("/subscription-plans", payload);
        toast.success(`Plan "${form.name}" created`);
      }
      setShowCreate(false);
      fetchPlans();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save plan");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletePlan) return;
    try {
      await API.delete(`/subscription-plans/${deletePlan.id}`);
      toast.success(`Plan "${deletePlan.name}" deleted`);
      setDeletePlan(null);
      fetchPlans();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete plan");
    }
  };

  const handleToggleActive = async (plan) => {
    try {
      await API.put(`/subscription-plans/${plan.id}`, { active: !plan.active });
      toast.success(`Plan "${plan.name}" ${plan.active ? "deactivated" : "activated"}`);
      fetchPlans();
    } catch {
      toast.error("Failed to update plan");
    }
  };

  const addFeature = () => setForm(prev => ({ ...prev, features: [...prev.features, ""] }));
  const removeFeature = (idx) => setForm(prev => ({ ...prev, features: prev.features.filter((_, i) => i !== idx) }));
  const updateFeature = (idx, val) => setForm(prev => {
    const features = [...prev.features];
    features[idx] = val;
    return { ...prev, features };
  });

  if (loading) return <div className="text-center py-20 text-muted-foreground">Loading plans...</div>;

  return (
    <div data-testid="subscription-plans-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
            <CreditCard size={18} className="text-white" weight="fill" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight">Subscription Plans</h1>
            <p className="text-xs text-muted-foreground">Create and manage billing plans for your tenants</p>
          </div>
        </div>
        <Button onClick={openCreate} data-testid="create-plan-btn">
          <Plus size={16} className="mr-1" /> Create Plan
        </Button>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="plans-grid">
        {plans.map(plan => (
          <div
            key={plan.id}
            className={`border rounded-lg overflow-hidden transition-all ${plan.active ? "border-border bg-card" : "border-dashed border-muted-foreground/30 bg-muted/20 opacity-70"}`}
            data-testid={`plan-card-${plan.id}`}
          >
            <div className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-bold font-[Chivo]">{plan.name}</h3>
                    {!plan.active && <Badge variant="secondary" className="text-[8px]">Inactive</Badge>}
                  </div>
                  <p className="text-[10px] text-muted-foreground font-mono mt-0.5">{plan.id}</p>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(plan)} data-testid={`edit-plan-${plan.id}`}>
                    <PencilSimple size={14} />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => setDeletePlan(plan)} data-testid={`delete-plan-${plan.id}`}>
                    <Trash size={14} />
                  </Button>
                </div>
              </div>

              {/* Pricing */}
              <div className="flex items-baseline gap-2 mb-3">
                {plan.monthly_price != null ? (
                  <>
                    <span className="text-2xl font-bold font-[Chivo]">
                      <CurrencyGbp size={16} className="inline -mt-1" weight="bold" />{plan.monthly_price}
                    </span>
                    <span className="text-xs text-muted-foreground">/month</span>
                  </>
                ) : plan.one_time_price != null ? (
                  <>
                    <span className="text-2xl font-bold font-[Chivo]">
                      <CurrencyGbp size={16} className="inline -mt-1" weight="bold" />{plan.one_time_price}
                    </span>
                    <span className="text-xs text-muted-foreground">one-time</span>
                  </>
                ) : (
                  <span className="text-sm text-muted-foreground">No price set</span>
                )}
              </div>

              {/* Trial */}
              <div className="flex items-center gap-1.5 mb-3 text-xs text-muted-foreground">
                <Clock size={12} />
                <span>{plan.trial_days > 0 ? `${plan.trial_days}-day free trial` : "No trial"}</span>
              </div>

              {/* Tenants count */}
              <div className="flex items-center gap-1.5 mb-3 text-xs">
                <Users size={12} className="text-muted-foreground" />
                <span className="text-muted-foreground">{tenantCounts[plan.id] || 0} tenant(s) on this plan</span>
              </div>

              {/* Limits */}
              <div className="grid grid-cols-2 gap-2 mb-3 text-[10px]">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Users size={10} /> {plan.limits?.users === -1 ? "Unlimited" : plan.limits?.users} Users
                </div>
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Folder size={10} /> {plan.limits?.projects === -1 ? "Unlimited" : plan.limits?.projects} Projects
                </div>
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Truck size={10} /> {plan.limits?.suppliers === -1 ? "Unlimited" : plan.limits?.suppliers} Suppliers
                </div>
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <HardDrive size={10} /> {plan.limits?.storage_gb === -1 ? "Unlimited" : `${plan.limits?.storage_gb}GB`} Storage
                </div>
              </div>

              {/* Features */}
              {plan.features?.length > 0 && (
                <div className="space-y-1 mb-3">
                  {plan.features.map((f, i) => (
                    <div key={i} className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
                      <CheckCircle size={12} className="text-emerald-500 shrink-0 mt-0.5" weight="fill" />
                      {f}
                    </div>
                  ))}
                </div>
              )}

              {/* Active Toggle */}
              <div className="flex items-center justify-between pt-3 border-t border-border">
                <span className="text-xs text-muted-foreground">Active</span>
                <Switch checked={plan.active} onCheckedChange={() => handleToggleActive(plan)} data-testid={`toggle-active-${plan.id}`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {plans.length === 0 && (
        <div className="text-center py-16 border border-dashed border-border rounded-md">
          <p className="text-muted-foreground mb-2">No subscription plans yet</p>
          <Button variant="outline" size="sm" onClick={openCreate}>Create your first plan</Button>
        </div>
      )}

      {/* Trial Expiry Notifications */}
      <div className="border border-border rounded-lg overflow-hidden mt-6" data-testid="trial-notifications-section">
        <div className="px-4 py-3 bg-muted/30 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <EnvelopeSimple size={14} className="text-muted-foreground" />
            <h3 className="text-sm font-semibold font-[Chivo]">Trial Expiry Notifications</h3>
            <Badge variant="secondary" className="text-[9px]">{trialNotifications.length} sent</Badge>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="text-xs h-7"
            disabled={sendingNotifications}
            onClick={async () => {
              setSendingNotifications(true);
              try {
                const { data } = await API.post("/trial-notifications/send-now");
                toast.success(`Sent ${data.sent} notification(s) to ${data.tenants_checked} trialing tenant(s)`);
                const { data: notifs } = await API.get("/trial-notifications");
                setTrialNotifications(notifs.notifications || []);
              } catch { toast.error("Failed to send"); }
              finally { setSendingNotifications(false); }
            }}
            data-testid="send-trial-notifications-btn"
          >
            <PaperPlaneTilt size={12} className="mr-1" />
            {sendingNotifications ? "Sending..." : "Send Now"}
          </Button>
        </div>
        <p className="px-4 py-2 text-[10px] text-muted-foreground bg-muted/10 border-b border-border">
          Automatic emails are sent every 12 hours to tenants whose trial expires within 2 days. Use "Send Now" to trigger manually.
        </p>
        {trialNotifications.length === 0 ? (
          <p className="px-4 py-6 text-xs text-muted-foreground text-center">No trial notifications sent yet</p>
        ) : (
          <div className="divide-y divide-border max-h-[300px] overflow-y-auto">
            {trialNotifications.map(n => (
              <div key={n.id} className="flex items-center justify-between px-4 py-2.5" data-testid={`trial-notif-${n.id}`}>
                <div>
                  <p className="text-xs font-medium">{n.tenant_name}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {n.recipients?.join(", ")} — {n.days_remaining} day{n.days_remaining !== 1 ? "s" : ""} remaining
                  </p>
                </div>
                <div className="text-right flex items-center gap-2">
                  {n.manual && <Badge variant="outline" className="text-[8px]">Manual</Badge>}
                  <span className="text-[10px] text-muted-foreground">
                    {new Date(n.sent_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="plan-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">{editPlan ? "Edit Plan" : "Create New Plan"}</DialogTitle>
            <DialogDescription>{editPlan ? "Update this subscription plan." : "Define a new subscription plan with pricing and limits."}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Plan Name *</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="e.g. Premium" data-testid="plan-name-input" />
              </div>
              <div>
                <Label>Plan ID {editPlan && <span className="text-muted-foreground">(read-only)</span>}</Label>
                <Input
                  value={editPlan ? form.id : (form.id || form.name.toLowerCase().replace(/\s+/g, "_"))}
                  onChange={e => !editPlan && setForm({ ...form, id: e.target.value })}
                  disabled={!!editPlan}
                  placeholder="auto-generated"
                  className="font-mono text-xs"
                  data-testid="plan-id-input"
                />
              </div>
            </div>

            {/* Pricing */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-2">PRICING</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Monthly Price</Label>
                  <Input type="number" step="0.01" min="0" value={form.monthly_price} onChange={e => setForm({ ...form, monthly_price: e.target.value })} placeholder="29.00" data-testid="plan-monthly-price" />
                </div>
                <div>
                  <Label>Annual Price</Label>
                  <Input type="number" step="0.01" min="0" value={form.annual_price} onChange={e => setForm({ ...form, annual_price: e.target.value })} placeholder="290.00" data-testid="plan-annual-price" />
                </div>
                <div>
                  <Label>One-Time Price</Label>
                  <Input type="number" step="0.01" min="0" value={form.one_time_price} onChange={e => setForm({ ...form, one_time_price: e.target.value })} placeholder="999.00" data-testid="plan-onetime-price" />
                </div>
              </div>
            </div>

            {/* Trial */}
            <div>
              <Label>Free Trial (days)</Label>
              <Input type="number" min="0" max="365" value={form.trial_days} onChange={e => setForm({ ...form, trial_days: e.target.value })} data-testid="plan-trial-days" />
              <p className="text-[10px] text-muted-foreground mt-1">Set to 0 for no trial. New signups on this plan will get this many days free.</p>
            </div>

            {/* Limits */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-2">RESOURCE LIMITS <span className="font-normal">(use -1 for unlimited)</span></p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="flex items-center gap-1"><Users size={12} /> Max Users</Label>
                  <Input type="number" min="-1" value={form.limits.users} onChange={e => setForm({ ...form, limits: { ...form.limits, users: e.target.value } })} data-testid="plan-limit-users" />
                </div>
                <div>
                  <Label className="flex items-center gap-1"><Folder size={12} /> Max Projects</Label>
                  <Input type="number" min="-1" value={form.limits.projects} onChange={e => setForm({ ...form, limits: { ...form.limits, projects: e.target.value } })} data-testid="plan-limit-projects" />
                </div>
                <div>
                  <Label className="flex items-center gap-1"><Truck size={12} /> Max Suppliers</Label>
                  <Input type="number" min="-1" value={form.limits.suppliers} onChange={e => setForm({ ...form, limits: { ...form.limits, suppliers: e.target.value } })} data-testid="plan-limit-suppliers" />
                </div>
                <div>
                  <Label className="flex items-center gap-1"><HardDrive size={12} /> Storage (GB)</Label>
                  <Input type="number" min="-1" value={form.limits.storage_gb} onChange={e => setForm({ ...form, limits: { ...form.limits, storage_gb: e.target.value } })} data-testid="plan-limit-storage" />
                </div>
              </div>
            </div>

            {/* Features */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-muted-foreground">FEATURES</p>
                <Button type="button" variant="ghost" size="sm" className="h-6 text-[10px]" onClick={addFeature} data-testid="add-feature-btn">
                  <Plus size={10} className="mr-1" /> Add
                </Button>
              </div>
              <div className="space-y-2">
                {form.features.map((f, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-emerald-500 shrink-0" weight="fill" />
                    <Input
                      value={f}
                      onChange={e => updateFeature(i, e.target.value)}
                      placeholder={`Feature ${i + 1}`}
                      className="text-xs h-8"
                      data-testid={`plan-feature-${i}`}
                    />
                    {form.features.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => removeFeature(i)}>
                        <X size={12} />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="save-plan-btn">
                {saving ? "Saving..." : editPlan ? "Update Plan" : "Create Plan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deletePlan} onOpenChange={(open) => !open && setDeletePlan(null)}>
        <DialogContent className="max-w-sm" data-testid="delete-plan-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo] text-destructive">Delete Plan</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <span className="font-semibold text-foreground">{deletePlan?.name}</span>?
              {(tenantCounts[deletePlan?.id] || 0) > 0 && (
                <span className="block mt-2 text-amber-600 font-medium">
                  {tenantCounts[deletePlan?.id]} tenant(s) are currently on this plan. You must move them first or deactivate the plan instead.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletePlan(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-plan-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
